import type { Permission, Role } from '@sentra/contracts';
import { roleHasPermission } from '@sentra/contracts';

import type { ManualAction } from '@/features/persons/table/acknowledgment';

/**
 * Which row actions a role is OFFERED, derived from the one grant table.
 *
 * **Hiding an action is a courtesy; it is not the control.** Every route is authorized twice at the
 * server (AD-36), and nothing here can change that. What this prevents is an operator being shown a
 * door they cannot open, which at a desk with somebody waiting is worse than not being shown it at
 * all.
 *
 * **It reads `ROLE_GRANTS` rather than restating it.** That table lives in `@sentra/contracts`
 * precisely so the browser and the API answer this question from one place: a verb the server stops
 * granting disappears from this menu on the next deploy, with nothing to remember. A second table
 * here would fail in both directions and silently — an action offered and refused, or an action
 * granted and never shown.
 */

/** The permission each manual action needs, matching the route each one reaches. */
export const ACTION_PERMISSIONS: Readonly<Record<ManualAction, Permission>> = {
  // Opening a record decrypts it, which is the action AD-37's exfiltration threshold counts, so it
  // is the stronger of the two read verbs rather than `person:read`.
  view: 'person:read_full',
  modify: 'person:write',
  presence: 'presence:submit',
  /**
   * AD-10's erasure, which is super-admin only.
   *
   * It is a row action so that the grant is OBSERVABLE: `person:shred` is held by exactly one role,
   * so the menu an operator sees and the menu a super admin sees differ by this entry, and the
   * difference is what the authorization test asserts. Choosing it opens the record — the dialog
   * demands the record's own short reference typed out, which nobody can supply from a list.
   */
  shred: 'person:shred',
};

/** The actions in the order they are offered, so a menu never reorders itself between roles. */
export const ACTION_ORDER: readonly ManualAction[] = ['view', 'modify', 'presence', 'shred'];

/** The actions this role holds the verb for. */
export function actionsFor(role: Role): readonly ManualAction[] {
  return ACTION_ORDER.filter((action) => roleHasPermission(role, ACTION_PERMISSIONS[action]));
}
